import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title:
		"Yahoo!ショッピング 商品画像「ファイルサイズが大きすぎます」の解決方法",
	description:
		"Yahoo!ショッピングで「ファイルサイズが大きすぎます」が出る原因（500KB・2MB制限）と、商品詳細画像・メイン画像を規格内に収める具体的な圧縮手順を解説。",
	publishDate: "2026-03-16",
	slug: "yahoo-shopping-image-file-size-error",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function YahooShoppingImageFileSizeError() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section className="mb-8">
						<p className="text-gray-700 leading-relaxed">
							Yahoo!ショッピングのストアクリエイターProで画像をアップロードしようとしたら「ファイルサイズが大きすぎます」と表示されてしまった——このエラー、思った以上に厳しい制限が原因です。
							Yahoo!ショッピングは画像の種類によってファイルサイズ上限が異なり、特に商品詳細画像・追加画像は500KBという厳しい制限があります。
							この記事で原因と解決手順を詳しく解説します。
						</p>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">
							エラーの原因：画像種別ごとに異なるサイズ制限
						</h2>
						<p className="text-gray-700 leading-relaxed mb-4">
							Yahoo!ショッピングでは、画像の用途ごとにファイルサイズの上限が異なります。
							「商品詳細画像」「追加画像」では<strong>500KB以下</strong>
							という非常に厳しい制限があります。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4">
							<table className="w-full text-sm text-gray-700">
								<thead>
									<tr className="border-b">
										<th className="text-left py-2 font-semibold">画像種別</th>
										<th className="text-left py-2 font-semibold">解像度上限</th>
										<th className="text-left py-2 font-semibold">
											ファイルサイズ
										</th>
										<th className="text-left py-2 font-semibold">形式</th>
									</tr>
								</thead>
								<tbody>
									<tr className="border-b">
										<td className="py-2">商品画像（メイン）</td>
										<td className="py-2">1,200 × 1,200px以内</td>
										<td className="py-2 font-medium">2MB以下</td>
										<td className="py-2">GIF、JPEG</td>
									</tr>
									<tr className="border-b">
										<td className="py-2">商品詳細画像</td>
										<td className="py-2">1,250 × 1,250px以内</td>
										<td className="py-2 font-medium text-red-600">500KB以下</td>
										<td className="py-2">GIF、JPEG、PNG</td>
									</tr>
									<tr>
										<td className="py-2">追加画像</td>
										<td className="py-2">1,250 × 1,250px以内</td>
										<td className="py-2 font-medium text-red-600">500KB以下</td>
										<td className="py-2">GIF、JPEG、PNG</td>
									</tr>
								</tbody>
							</table>
						</div>
						<p className="text-gray-700 text-sm">
							※一括アップロード（ZIP形式）の場合はZIPファイル自体を50MB以内に分割する必要があります。
						</p>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">解決手順</h2>
						<ol className="list-decimal list-inside space-y-4 text-gray-700">
							<li>
								<strong>アップロードする画像の種別を確認する</strong>
								<p className="mt-1 ml-5 text-sm">
									メイン商品画像なら2MB以下に、詳細・追加画像なら500KB以下に収める必要があります。
									まずどちらの画像を使うか確認してから圧縮目標を設定しましょう。
								</p>
							</li>
							<li>
								<strong>解像度を指定サイズ以下にリサイズする</strong>
								<p className="mt-1 ml-5 text-sm">
									メイン画像は1,200×1,200px以内、詳細・追加は1,250×1,250px以内にリサイズします。
									ファイルサイズはピクセル数に比例するため、リサイズだけで大幅に削減できます。
									Windowsペイントやmacのプレビューでリサイズ可能です。
								</p>
							</li>
							<li>
								<strong>JPEG圧縮で500KB以下に収める</strong>
								<p className="mt-1 ml-5 text-sm">
									Squoosh（squoosh.app）でJPEG品質を60〜75%に設定して出力します。
									品質60%でも商品画像としては十分なクオリティが維持できます。
									ファイルサイズをリアルタイムで確認しながら圧縮率を調整できます。
								</p>
							</li>
							<li>
								<strong>ストアクリエイターProで再アップロード</strong>
								<p className="mt-1 ml-5 text-sm">
									「商品管理 →
									商品画像の設定」から修正した画像を再アップロードします。
								</p>
							</li>
						</ol>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">
							しろぬきやで自動変換する方法
						</h2>
						<p className="text-gray-700 leading-relaxed">
							500KBの制限はかなり厳しく、毎回手動で圧縮するのは大変です。
							<strong>しろぬきや</strong>
							では、Yahoo!ショッピング向けの出力プリセットで
							1,200×1,200px・500KB以下への自動変換が可能です。
							AI背景除去と同時にリサイズ・圧縮も一括処理できます。
						</p>
					</section>

					<CtaBlock />
					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
